import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Bank } from '@synapex/prisma/client'

import { useSchemaWithBic, FormValues, ContragentType } from './schema'
import { NAME_PREFIX } from './values'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import { DadataCompanyData } from '@synapex/shared2/gateway-rest-api'

export type UseContragentFormProps = {
    defaultValues: FormValues
    defaultType: ContragentType
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
    onSubmitValid: (
        v: FormValues,
        type: ContragentType,
        bankInfo: Bank | undefined | null
    ) => Promise<void>
} & WorkspaceDescriptor

export const maybeSetContragentTypePrefix = ({
    previousType,
    newType,
    getValues,
    setValue,
}: {
    previousType: ContragentType
    newType: ContragentType
    getValues: (name: 'name') => string
    setValue: (name: 'name', v: string) => void
}) => {
    if (previousType === newType) {
        return
    }

    const nameValue = getValues('name')
    const trimmedNameValue = nameValue.trim()

    if (!trimmedNameValue) {
        //name field is empty, fill it with prefix
        // setValue('name', NAME_PREFIX[newType])
    } else {
        const trimmedStartNameValue = nameValue.trimStart()

        // if (!NAME_PREFIX[previousType]) {
        //     //current type has no prefix, insert prefix of the new one
        //     setValue('name', NAME_PREFIX[newType] + trimmedStartNameValue)
        // } else {
        const nonEmptyPrefixes = Object.values(NAME_PREFIX).filter((p) =>
            Boolean(p)
        )
        for (const np of nonEmptyPrefixes) {
            //replace ИП or ООО
            if (trimmedStartNameValue.startsWith(np)) {
                setValue(
                    'name',
                    NAME_PREFIX[newType] +
                        trimmedStartNameValue.slice(np.length)
                )
                break
            }
        }
        // }
    }
}

const getCompanyName = (dadata: DadataCompanyData, type: ContragentType) => {
    // Для ИП возвращаем без приписки "ИП"
    if (type === 'ip') {
        return (
            dadata.name.full ||
            dadata.name.shortWithOpf ||
            dadata.name.fullWithOpf
        )
    }
    return dadata.name.shortWithOpf || dadata.name.fullWithOpf
}

export function useContragentForm({
    defaultValues,
    defaultType,
    workspace_type,
    workspace_id,
    roleType,
    onSubmitValid,
}: UseContragentFormProps) {
    const [userSelectedType, setUserSelectedType] =
        useState<ContragentType | null>(defaultValues.inn ? defaultType : null)

    const { schema, bankInfo, dadataInfo, innChecked, innVariant } =
        useSchemaWithBic(
            userSelectedType ?? defaultType,
            defaultValues,
            {
                workspace_type,
                workspace_id,
            },
            roleType
        )

    const type = useMemo(() => {
        if (userSelectedType !== null) {
            return userSelectedType
        }
        if (dadataInfo?.type) {
            return dadataInfo.type
        }
        if (innVariant) {
            return innVariant === 'juridical' ? 'juridical' : 'physical'
        }
        return defaultType
    }, [userSelectedType, dadataInfo, defaultType, innVariant])

    const { formState, handleSubmit, control, getValues, setValue } =
        useForm<FormValues>({
            mode: 'onChange',
            resolver: zodResolver(schema) as never,
            defaultValues,
        })

    const formHasErrors =
        !formState.isValid || Object.keys(formState.errors).length > 0

    const onSubmit = handleSubmit((formValues: FormValues) =>
        onSubmitValid(formValues, type, bankInfo)
    )

    const dadataAppliedRef = useRef<DadataCompanyData | null>(null)

    useEffect(() => {
        if (!dadataInfo) {
            return
        }

        if (dadataAppliedRef.current?.inn === dadataInfo.inn) {
            return
        }

        dadataAppliedRef.current = dadataInfo

        const companyName = getCompanyName(dadataInfo, type)

        if (companyName) {
            setValue('name', companyName)
        }

        if (dadataInfo.management) {
            if (dadataInfo.management.name) {
                setValue('fullName', dadataInfo.management.name)
            }
            if (dadataInfo.management.post) {
                setValue('position', dadataInfo.management.post)
            }
        }

        if (dadataInfo.phone) {
            setValue('phone', dadataInfo.phone)
        }

        if (dadataInfo.email) {
            setValue('email', dadataInfo.email)
        }

        if (dadataInfo.kpp) {
            setValue('kpp', dadataInfo.kpp)
        }

        if (dadataInfo.ogrn) {
            setValue('ogrn', dadataInfo.ogrn)
        }

        if (dadataInfo.address?.value) {
            setValue('legalAddress', dadataInfo.address.value)
            setValue('actualAddress', dadataInfo.address.value)
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUserSelectedType(dadataInfo.type)
    }, [dadataInfo, setValue, type])

    const onChangeType = useCallback(
        (newType: ContragentType) => {
            maybeSetContragentTypePrefix({
                getValues,
                setValue,
                previousType: type,
                newType,
            })
            setUserSelectedType(newType)
        },
        [type, getValues, setValue]
    )

    // ИНН валиден если это редактирование (есть defaultValues.inn) или получены данные из DaData
    const isInnValid = Boolean(defaultValues.inn) || innChecked

    const optionVariant: 'physical' | 'juridical' = defaultValues.inn
        ? type === 'juridical' || type === 'autonomous_non_profit_organization'
            ? 'juridical'
            : 'physical'
        : (innVariant ?? 'physical')

    return {
        formState,
        control,
        onSubmit,
        formHasErrors,
        bankInfo,
        type,
        onChangeType,
        isInnValid,
        optionVariant,
    }
}

export type UseContragentForm = ReturnType<typeof useContragentForm>
